import { ComponentType, Fragment, ReactNode, useState } from 'react';

import { Tab as HeadlessTab } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { Slide } from '@components/animators/slide';
import { Badge } from '@components/badge';
import { useAriaId, usePreviousValue } from '@components/component-helpers';
import { IconProps } from '@components/icons';
import { IconErrorSmall } from '@components/icons/error-small';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export interface HorizontalTabItem {
  label: string;
  icon?: ComponentType<IconProps>;
  content: ReactNode;
  onClick?: () => void;
  tabCount?: number;
  hasError?: boolean;
}

export interface HorizontalTabProps {
  items: Array<HorizontalTabItem>;
  defaultIndex?: number;
  onTabChange?: (index: number) => void;
}

export function HorizontalTabs({ items, defaultIndex = 0, onTabChange }: HorizontalTabProps) {
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  const id = useAriaId('horizontal-tabs');

  const previousIndex = usePreviousValue(selectedIndex) ?? defaultIndex;

  const onChangeTab = (index: number) => {
    setSelectedIndex(index);
    onTabChange?.(index);
  };
  return (
    <HeadlessTab.Group selectedIndex={selectedIndex} onChange={onChangeTab}>
      <HeadlessTab.List as={HorizontalTabContainer}>
        {items.map((item) => (
          <HeadlessTab key={item.label} as={StyledHorizontalTabItem} onClick={item.onClick}>
            {({ selected }) => (
              <>
                <Stack space={1} orientation="horizontal" align="center">
                  {item.icon && <item.icon size={16} />}
                  <div>{item.label}</div>
                  {Number.isInteger(item.tabCount) && <Badge text={item.tabCount!} />}
                  {item.hasError && <IconErrorSmall color="red100" />}
                </Stack>
                <AnimatePresence initial={false}>
                  {selected && <FloatingTabBorder layoutId={id} />}
                </AnimatePresence>
              </>
            )}
          </HeadlessTab>
        ))}
      </HeadlessTab.List>
      <HeadlessTab.Panels as={Fragment}>
        <AnimatePresence initial={false}>
          {items.map((item, index) => (
            <HeadlessTab.Panel key={item.label}>
              <Slide offset={previousIndex < index ? 20 : -20}>{item.content}</Slide>
            </HeadlessTab.Panel>
          ))}
        </AnimatePresence>
      </HeadlessTab.Panels>
    </HeadlessTab.Group>
  );
}

const HorizontalTabContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: pxToRem(16),
  paddingLeft: pxToRem(24),
  width: '100%',
  borderBottom: `${pxToRem(1)} solid ${theme.color.uiTrans10}`,

  '[data-headlessui-focus-visible] &:focus-within': {
    boxShadow: theme.shadow.focus,
    borderRadius: pxToRem(4),
    '--shadow-focus-color': theme.color.elementBlue100,
    '--shadow-focus-gap': 0,
    '--shadow-focus-width': pxToRem(2),
  },
});

const StyledHorizontalTabItem = styled.button({
  font: theme.font.semibold14,
  padding: 0,
  background: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: theme.base.height,
  marginBottom: `-${pxToRem(1)}`,
  outline: 'none',
  svg: {
    transition: 'color .3s ease-out',
  },

  '&[aria-selected="true"]': {
    '--icon-color': theme.color.elementPurpleInvert,
    color: theme.color.textPrimary,
  },

  '&[aria-selected="false"]': {
    color: theme.color.textHelper,
    borderBottom: `${pxToRem(4)} solid transparent`,
    '--icon-color': theme.color.elementPurple50,

    '&:hover': {
      cursor: 'pointer',
      color: theme.color.textPrimary,
      '--icon-color': theme.color.elementPurpleInvert,
      borderColor: theme.color.uiTrans10,
      transition: 'all .3s ease-out',
    },
  },
});

const FloatingTabBorder = styled(motion.div)({
  width: '100%',
  height: 4,
  flexShrink: 0,
  background: theme.color.elementPurpleInvert,
});
